import { forEach, groupBy, keyBy, mapValues, pickBy } from 'lodash'
import { storeToRefs } from 'pinia'

import api from '../api/index'
import * as mutationTypes from './mutation-types'
import { SET_TITLES } from '@app/application/client_meetings/screens/prepare/stores/mutation-types'
import remarkTags from './remark-tags'
import { SfLog } from '@app/adapters/logger'

import { useAuditsStore } from '@app/application/sidebar-audit-log/store/audits'
import { useCommsFilterStore } from '@app/application/communication/store/comms-filters'
import { useContext } from '@app/shared/pinia/context'
import { useHighlightedCard } from '@app/shared/pinia/highlighted-card'

// In presentation mode the comms drawer shares the meeting store, which holds
// every talking point for the meeting. Scope it to the resource being presented
// so the drawer shows only that resource's talking points. A no-op elsewhere.
function scopeToPresentedResource(remarks, state) {
  if (!window.Silverfin?.presentationMode || !state.ownerType) return remarks

  return pickBy(
    remarks,
    (remark) =>
      remark.contextType === state.ownerType &&
      remark.contextId === state.contextId
  )
}

// this contains shared logic included in multiple stores, in the root of the store (not as a submodule)
export default (
  endpointParams,
  initialData,
  fetchCommunicationFn = api.getContextCommunication
) => {
  return {
    getters: {
      hasContext: (state) => {
        return state.contextType !== undefined ? true : false
      },

      ledgerId: (state) => {
        return state.ledgerId
      },

      communicationItemType: (state) => {
        return state.communicationItemType
      },

      communicationItemId: (state) => {
        return state.communicationItemId
      },

      contextType: (state) => {
        return state.contextType
      },

      contextHrefParam: (state) => {
        return state.contextHrefParam
      },

      contextId: (state) => {
        return state.contextId
      },
      // filter Remarks by workflow review active tag
      filteredNotes: (_state, getters) => {
        const filterStore = useCommsFilterStore()
        const { typeFilter } = storeToRefs(filterStore)

        if (typeFilter.value !== null && typeFilter.value !== 'note') return {}

        return getters.filteredRemarks('notes')
      },
      filteredTodos: (_state, getters) => {
        const filterStore = useCommsFilterStore()
        const { typeFilter } = storeToRefs(filterStore)

        if (typeFilter.value !== null && typeFilter.value !== 'todo') return {}

        return getters.filteredRemarks('todos')
      },
      filteredOpenTodos: (_state, getters) => {
        const filterStore = useCommsFilterStore()
        const { typeFilter } = storeToRefs(filterStore)

        if (typeFilter.value !== null && typeFilter.value !== 'todo') return {}

        return getters.filteredRemarks('openTodos')
      },
      filteredDoneTodos: (_state, getters) => {
        const filterStore = useCommsFilterStore()
        const { typeFilter } = storeToRefs(filterStore)

        if (typeFilter.value !== null && typeFilter.value !== 'todo') return {}

        return getters.filteredRemarks('doneTodos')
      },
      filteredSfAssistantRemarks: (state, getters) => {
        return getters.filteredRemarks('sfAssistantCheckTodos')
      },
      filteredRemarks: (state, getters) => (remarkType) => {
        const remarks = getters[remarkType]

        const { workflowReviewActiveTagId } = state.remarkTags

        if (workflowReviewActiveTagId) {
          return pickBy(remarks, (remark) =>
            remark.tagIds.includes(workflowReviewActiveTagId)
          )
        }

        return scopeToPresentedResource(remarks, state)
      },
      workflowReviewActiveTag: (state) => {
        return state.remarkTags.workflowReviewTags.find(
          (t) => t.id === state.remarkTags.workflowReviewActiveTagId
        )
      },
      filteredCheckLists: (state) => {
        const filterStore = useCommsFilterStore()
        const { typeFilter } = storeToRefs(filterStore)

        if (typeFilter.value !== null && typeFilter.value !== 'checklist')
          return {}

        return state.checks.checkLists[state.ledgerId] || {}
      },
      loadingMentionUsernames: (state) => state.loading.mentionUsernames,
    },

    mutations: {
      [mutationTypes.SET_COMMUNICATION_CONTEXT](
        state,
        { contextType, contextId, contextHrefParam }
      ) {
        state.contextType = contextType
        state.contextId = contextId
        state.contextHrefParam = contextHrefParam
      },
      [mutationTypes.SET_LOADING](state, { type, value }) {
        state.loading[type] = value
      },
    },

    actions: {
      async fetchCommunication(
        { commit, dispatch, state },
        { action, remark } = {}
      ) {
        commit(mutationTypes.START_FETCHING_COMMUNICATIONS)

        try {
          const data = await fetchCommunicationFn(this.state)

          commit(mutationTypes.STOP_FETCHING_COMMUNICATIONS)
          const communication = data?.communication

          if (!communication) {
            throw new Error('communications response returned empty')
          }

          // key contexts by type (Account, ReconciliationText...) and then by id
          // to avoid id collisions of different type
          const contexts = mapValues(
            groupBy(communication.contexts, 'ledgerId'),
            (v) => mapValues(groupBy(v, 'type'), (v) => keyBy(v, 'id'))
          )
          commit(mutationTypes.SET_CONTEXTS, contexts)

          if (remark) {
            commit(mutationTypes.REMARK_CHANGED, { action, remark })
          }

          commit(mutationTypes.SET_REMARKS, {
            ...keyBy(communication.notes, 'id'),
            ...keyBy(communication.todos, 'id'),
            ...keyBy(communication.sfAssistantCheckTodos, 'id'),
          })

          if (communication.titles) {
            commit(SET_TITLES, keyBy(communication.titles, 'id'))
          }

          // Guard is load-bearing: only the client-meeting-prep store registers the
          // setInitialOrder action. Other buildComms consumers (inbox, workflow-review)
          // don't return `order`, so this never fires for them. If a new consumer starts
          // returning order, it must also register the setInitialOrder action.
          if (communication.order?.length) {
            dispatch('setInitialOrder', communication.order)
          }

          // index check ids for check lists on the front-end for now
          const checkLists = mapValues(
            groupBy(communication.checkLists, 'ledgerId'),
            (v) => keyBy(v, 'id')
          )
          forEach(checkLists, (clGroup) =>
            forEach(clGroup, (cl) => {
              cl['checkIds'] = []
              for (const check of communication.checks) {
                if (check.checkListId === cl.id) {
                  cl['checkIds'].push(check.id)
                }
              }
            })
          )
          commit(mutationTypes.SET_CHECK_LISTS, checkLists)
          commit(mutationTypes.SET_CHECKS, keyBy(communication.checks, 'id'))
          commit(mutationTypes.SET_USERS, keyBy(communication.users, 'id'))
          commit(
            mutationTypes.SET_ATTACHMENTS,
            keyBy(communication.attachments, 'id')
          )
          commit(
            mutationTypes.SET_COMMENTS,
            keyBy(communication.comments, 'id')
          )
          commit(
            mutationTypes.SET_CHECKINS,
            keyBy(communication.checkins, 'id')
          )

          const allRemarkTags = keyBy(communication.allRemarkTags, 'id')
          commit(mutationTypes.SET_ALL_REMARK_TAGS, allRemarkTags)

          // TODO: quite hacky, find a better way to pass all-remark-tags to live-inputs (jquery-based)
          window.allRemarkTags = allRemarkTags
        } catch (e) {
          SfLog.warn('Failed to fetch communication', {
            extra: {
              error: e,
              ledgerId: state.ledgerId,
              ledgerPeriodName: state.ledgerPeriodName,
              communicationItemId: state.communicationItemId,
            },
          })
        }
      },

      fetchMentionUsernames({ commit }) {
        commit(mutationTypes.SET_LOADING, {
          type: 'mentionUsernames',
          value: true,
        })
        return api
          .getMentionUsernames({
            companyId:
              this.state.companyId || this.state.notifications.activeCompanyId,
          })
          .then((data) => {
            commit(mutationTypes.SET_MENTION_USERNAMES, data.usernames)
            commit(mutationTypes.SET_PULSE_USERNAMES, data.pulseUsernames)
          })
          .finally(() => {
            commit(mutationTypes.SET_LOADING, {
              type: 'mentionUsernames',
              value: false,
            })
          })
      },

      // Reset all communication data to it's default values
      resetCommunicationData({ commit }) {
        const filterStore = useCommsFilterStore()

        commit(mutationTypes.SET_REMARKS, {})
        commit(mutationTypes.SET_CHECK_LISTS, {})
        commit(mutationTypes.SET_CHECKS, {})
        commit(mutationTypes.SET_USERS, {})
        commit(mutationTypes.SET_ATTACHMENTS, {})
        commit(mutationTypes.SET_COMMENTS, {})
        commit(mutationTypes.SET_CHECKINS, {})

        filterStore.clearTypeFilter()
      },

      setCommunicationContext({ commit, dispatch }, communicationContext) {
        const auditsStore = useAuditsStore()
        const contextStore = useContext()
        const highlightedCardStore = useHighlightedCard()

        commit(mutationTypes.SET_COMMUNICATION_CONTEXT, communicationContext)
        commit(mutationTypes.RESET_FETCHING_COMMUNICATIONS)
        dispatch('fetchCommunication')
        // Notifications drive the unread indicators in the sidepane tabs and
        // remark cards; without a refetch here, opening the sidepane or
        // switching context shows stale read-state until the next full page
        // load.
        dispatch('fetchNotifications', Silverfin.currentCompanyId)
        highlightedCardStore.unsetHighlightedCard()

        // TODO: Split the context out of the comms store entirely
        // Until that is done keep a duplicate copy of it
        contextStore.setContext(
          communicationContext.contextType,
          communicationContext.contextId
        )

        // Eagerly load the changes since last visit to display the appropriate unread count UI, setCommunicationContext is used in Workflow Review only
        // Pulse users don't have access to audit endpoints, skip to avoid 403/404s
        if (!window.Silverfin?.isCurrentUserPulse?.()) {
          auditsStore.fetchChangesSinceLastVisit({
            contextId: communicationContext.contextId,
            contextType: communicationContext.contextType,
          })
        }
      },

      remarkChanged({ commit }, { action, remark }) {
        commit(mutationTypes.REMARK_CHANGED, { action, remark })
      },
    },

    state() {
      const { tagCloud } = initialData

      return {
        contextType: endpointParams.contextType,
        contextId: endpointParams.contextId,
        // The remark-side (class) name of the context, e.g. "Account",
        // "ReconciliationText". Used to scope the comms drawer in presentation mode.
        ownerType: endpointParams.ownerType,
        ledgerId: endpointParams.ledgerId,
        companyId: endpointParams.companyId,
        ledgerPeriodName: endpointParams.ledgerPeriodName,
        contextHrefParam: endpointParams.contextHrefParam,
        communicationItemId: endpointParams.communicationItemId,
        communicationItemType: endpointParams.communicationItemType,
        pastMeetings: endpointParams.pastMeetings,
        remarkTags: {
          ...remarkTags.state(),
          workflowReviewTags: tagCloud || [],
        },
        loading: {
          notifications: false,
          mentionUsernames: false,
        },
      }
    },
  }
}
